import { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import AdminService from "../../../services/admin.service";
import Select from "../../common/Select";
import GeneralInput from "../../common/GeneralInput";
import { useGlobal } from "../../../context/global";
import { CashRegisterCheckpoint, Seller } from "../../../types/common.types";
import formatNumberToString from "../../../utils/formatNumber";

function extractMessage(error: any) {
    return typeof error?.response?.data === 'string' ? error.response.data : 'Ocurrió un error, intenta de nuevo'
}

export default function AdminRegisterCheckpoint() {
    const { setIsLoading, setMessage } = useGlobal()
    const [sellers, setSellers] = useState<Seller[]>([])
    const [shiftDate, setShiftDate] = useState(format(new Date(), 'yyyy-MM-dd'))
    const [period, setPeriod] = useState<'AM' | 'PM'>('AM')
    const [checkpointType, setCheckpointType] = useState<'apertura' | 'cierre'>('cierre')
    const [sellerId, setSellerId] = useState('')
    const [countedAmount, setCountedAmount] = useState('')
    const [existingCheckpoint, setExistingCheckpoint] = useState<CashRegisterCheckpoint | null>(null)

    useEffect(() => {
        AdminService.listSellers().then(res => setSellers(res.data)).catch(console.log)
    }, [])

    // Si ya existe un checkpoint para esa fecha/turno/tipo, el formulario pasa a modo "corregir"
    // en vez de "registrar" — no hace falta elegir responsable, ya lo tiene.
    useEffect(() => {
        async function checkExisting() {
            try {
                const res = await AdminService.getCheckpoint(shiftDate, period, checkpointType)
                setExistingCheckpoint(res.data)
                setCountedAmount(res.data ? String(res.data.counted_amount) : '')
            } catch (error) {
                console.log(error)
            }
        }
        checkExisting()
    }, [shiftDate, period, checkpointType])

    async function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            if (existingCheckpoint) {
                await AdminService.correctCheckpoint(shiftDate, period, checkpointType, Number(countedAmount))
            } else if (checkpointType === 'apertura') {
                await AdminService.adminRegisterApertura(shiftDate, period, sellerId, Number(countedAmount))
            } else {
                await AdminService.adminRegisterCierre(shiftDate, period, sellerId, Number(countedAmount))
            }
            setMessage({ type: 'success', content: existingCheckpoint ? 'Checkpoint corregido' : 'Checkpoint registrado' })
            setTimeout(() => setMessage(undefined), 2500)
            const res = await AdminService.getCheckpoint(shiftDate, period, checkpointType)
            setExistingCheckpoint(res.data)
        } catch (error) {
            alert(extractMessage(error))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Main onSubmit={submit}>
            <h3>{existingCheckpoint ? 'Corregir apertura/cierre' : 'Registrar apertura/cierre atrasado'}</h3>
            <p className="hint">
                {existingCheckpoint
                    ? 'Ya existe un checkpoint para esta fecha/turno/tipo. Corrige el monto contado si se ingresó mal — el desfase se recalcula solo.'
                    : 'Uso excepcional: cuando un turno anterior quedó sin cerrar y eso bloquea a un vendedor para abrir su caja hoy. Registra aquí el conteo que faltó, a nombre de quien correspondía.'}
            </p>
            <div className="row">
                <label>
                    Fecha
                    <input type="date" value={shiftDate} onChange={e => setShiftDate(e.target.value)} required />
                </label>
                <Select
                    label="Período"
                    value={period}
                    options={[{ value: 'AM', name: 'AM' }, { value: 'PM', name: 'PM' }]}
                    onChange={e => setPeriod(e.target.value as 'AM' | 'PM')}
                />
                <Select
                    label="Tipo"
                    value={checkpointType}
                    options={[{ value: 'apertura', name: 'Apertura' }, { value: 'cierre', name: 'Cierre' }]}
                    onChange={e => setCheckpointType(e.target.value as 'apertura' | 'cierre')}
                />
            </div>
            {existingCheckpoint && (
                <div className="existing">
                    Responsable: <b>{existingCheckpoint.responsible_name || 'sin nombre'}</b> ·
                    Esperado: <b>${formatNumberToString(existingCheckpoint.expected_amount)}</b> ·
                    Contado actual: <b>${formatNumberToString(existingCheckpoint.counted_amount)}</b> ·
                    Diferencia: <b className={existingCheckpoint.difference < 0 ? 'negative' : ''}>{existingCheckpoint.difference > 0 ? '+' : ''}${formatNumberToString(existingCheckpoint.difference)}</b>
                </div>
            )}
            <div className="row">
                {!existingCheckpoint && (
                    <Select
                        label="Responsable"
                        required
                        defaultNull
                        value={sellerId}
                        options={sellers.map(s => ({ value: s.id, name: s.name }))}
                        onChange={e => setSellerId(e.target.value)}
                    />
                )}
                <GeneralInput
                    label={existingCheckpoint ? 'Nuevo monto contado' : 'Monto contado'}
                    type="number"
                    required
                    value={countedAmount}
                    onChange={e => setCountedAmount(e.target.value)}
                    placeholder="0"
                />
            </div>
            <button type="submit">{existingCheckpoint ? 'Corregir' : 'Registrar'}</button>
        </Main>
    )
}

const Main = styled.form`
display: flex;
flex-direction: column;
gap: 12px;
max-width: 600px;
padding: 15px;
border-radius: 10px;
background-color: ${props => props.theme.whiteBone};
h3 {
    font-size: 16px;
    font-weight: 500;
}
.hint {
    font-size: 13px;
    color: ${props => props.theme.gray};
}
.existing {
    font-size: 13px;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${props => props.theme.whiteBackground};
    .negative { color: ${props => props.theme.lightRed}; }
}
.row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    > * {
        flex: 1;
        min-width: 150px;
    }
    label {
        display: flex;
        flex-direction: column;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: ${props => props.theme.gray};
        input {
            padding: 8px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
        }
    }
}
button {
    align-self: flex-start;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    background-color: ${props => props.theme.blue};
    color: ${props => props.theme.white};
    font-weight: 600;
    cursor: pointer;
}
`
